// magnitude-dashboard/src/components/AddAgentWizard.jsx
import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { Icon } from './Brand'

const slugify = (s) =>
  s.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 31)

const SOURCES = [
  { id: 'prompt', icon: 'psychology', label: 'Prompt-only' },
  { id: 'mcp', icon: 'lan', label: 'MCP endpoint' },
  { id: 'files', icon: 'upload_file', label: 'Upload files' },
]

// The build-an-agent flow. Renders as a modal by default (launched from the
// Agents page) or inline (the Build tab) when `inline` is set — the verify /
// publish logic is identical either way, so there's a single source of truth.
export default function AddAgentWizard({ inline = false, onClose = () => {}, onCreated = () => {} }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [systemPrompt, setSystemPrompt] = useState('')
  const [dataSource, setDataSource] = useState('prompt') // 'prompt' | 'mcp' | 'files'
  const [mcpUrl, setMcpUrl] = useState('')
  const [mcpAuth, setMcpAuth] = useState('')
  const [fileNames, setFileNames] = useState([])
  const [sample, setSample] = useState('')
  const [verifyState, setVerifyState] = useState(null) // null | 'busy' | result obj
  const [publishing, setPublishing] = useState(false)
  const [published, setPublished] = useState('') // inline success banner (agent name)
  const [error, setError] = useState('')
  const fileRef = useRef(null)

  const slug = useMemo(() => slugify(name), [name])
  const toolName = slug ? `ask_${slug}_agent` : 'ask_<name>_agent'
  const canTest = slug && description.trim() && systemPrompt.trim()
  // Publish needs only valid fields — routing is advisory, not a gate. On a real
  // model the orchestrator may not route to a brand-new prompt-only agent for a
  // single sample, so hard-blocking on verify.routed would lock users out.
  const canPublish = canTest
  const routed = verifyState && verifyState !== 'busy' && verifyState.routed

  const fileNamesKey = fileNames.join('|')
  useEffect(() => {
    setVerifyState(null)
  }, [name, description, systemPrompt, dataSource, mcpUrl, mcpAuth, fileNamesKey])

  const agentPayload = () => {
    const docs = dataSource === 'files' ? fileNames : []
    // Mock retrieval: surface the referenced documents in the description so the
    // agent (and the Agents detail view) reflects them — no real ingestion.
    const desc = docs.length
      ? `${description.trim()}\n\nReference documents (demo): ${docs.join(', ')}`
      : description.trim()
    return {
      name: slug,
      description: desc,
      system_prompt: systemPrompt.trim(),
      ...(dataSource === 'mcp' && mcpUrl.trim()
        ? { mcp_endpoint: mcpUrl.trim(), mcp_auth_header: mcpAuth.trim() }
        : {}),
      ...(docs.length ? { note: `documents: ${docs.join(', ')}` } : {}),
    }
  }

  async function runVerify() {
    setError('')
    setVerifyState('busy')
    try {
      const res = await fetch('/api/agents/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Route on the raw description, not the doc-annotated one.
        body: JSON.stringify({ agent: agentPayload(), samples: [sample.trim() || description.trim()] }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { setError(data.error || `Verify failed (${res.status}).`); setVerifyState(null); return }
      setVerifyState(data)
    } catch {
      setError('Could not reach the backend. Is the bridge running on :8080?')
      setVerifyState(null)
    }
  }

  function resetForm() {
    setName(''); setDescription(''); setSystemPrompt('')
    setDataSource('prompt'); setMcpUrl(''); setMcpAuth(''); setFileNames([])
    setSample(''); setVerifyState(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  async function publish() {
    setError('')
    setPublished('')
    setPublishing(true)
    try {
      const res = await fetch('/api/agents', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(agentPayload()),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) { setError(data.error || `Publish failed (${res.status}).`); return }
      onCreated()
      if (inline) {
        // Inline (Build tab) has no modal to dismiss — confirm, then reset so the
        // admin can build another agent right away.
        setPublished(slug)
        resetForm()
      } else {
        onClose()
      }
    } catch {
      setError('Could not reach the backend.')
    } finally {
      setPublishing(false)
    }
  }

  const onFiles = (e) => {
    const files = Array.from(e.target.files || [])
    setFileNames(files.map((f) => f.name))
  }

  const form = (
    <>
      <label className="add-agent-label">Name</label>
      <input
        className="add-agent-input add-agent-name"
        value={name}
        placeholder="e.g. legal"
        onChange={(e) => setName(e.target.value)}
      />
      <div className="ph-sub mono" style={{ marginTop: 4 }}>tool: {toolName}</div>

      <label className="add-agent-label">Description (drives routing)</label>
      <input
        className="add-agent-input add-agent-desc"
        value={description}
        placeholder="Answer legal and compliance questions about contracts."
        onChange={(e) => setDescription(e.target.value)}
      />

      <label className="add-agent-label">System prompt</label>
      <textarea
        className="add-agent-input add-agent-prompt"
        rows={5}
        value={systemPrompt}
        placeholder="You are the legal specialist. Be precise and cite the relevant clause."
        onChange={(e) => setSystemPrompt(e.target.value)}
      />

      <label className="add-agent-label">Data source</label>
      <div className="ds-source-row">
        {SOURCES.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`ds-source ${dataSource === s.id ? 'active' : ''}`}
            onClick={() => setDataSource(s.id)}
          >
            <Icon name={s.icon} />
            {s.label}
          </button>
        ))}
      </div>

      {dataSource === 'prompt' && (
        <div className="ph-sub" style={{ marginTop: 8 }}>
          The agent answers from its system prompt alone — no external data.
        </div>
      )}

      {dataSource === 'mcp' && (
        <div style={{ marginTop: 8 }}>
          <input
            className="add-agent-input add-agent-mcp-url"
            value={mcpUrl}
            placeholder="http://127.0.0.1:8765/mcp"
            onChange={(e) => setMcpUrl(e.target.value)}
          />
          <input
            className="add-agent-input add-agent-mcp-auth"
            style={{ marginTop: 8 }}
            value={mcpAuth}
            placeholder="Authorization header (optional, e.g. Bearer …)"
            onChange={(e) => setMcpAuth(e.target.value)}
          />
          <div className="ph-sub" style={{ marginTop: 8 }}>
            <Icon name="info" style={{ fontSize: 15, verticalAlign: '-3px', marginRight: 4 }} />
            MCP (Model Context Protocol) lets an agent call live tools from an external server.
            Enter the server's URL — the agent discovers and uses its tools automatically.
            Demo server: <span className="mono">http://127.0.0.1:8765/mcp</span>
          </div>
        </div>
      )}

      {dataSource === 'files' && (
        <div style={{ marginTop: 8 }}>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="add-agent-input add-agent-files"
            onChange={onFiles}
          />
          {fileNames.length > 0 && (
            <div className="ctl-caps" style={{ marginTop: 8, marginLeft: 0 }}>
              {fileNames.map((f) => <span className="ctl-cap" key={f}>{f}</span>)}
            </div>
          )}
          <div className="ph-sub" style={{ marginTop: 8 }}>
            <Icon name="info" style={{ fontSize: 15, verticalAlign: '-3px', marginRight: 4 }} />
            Documents are referenced by the agent (demo) — not indexed for retrieval.
          </div>
        </div>
      )}

      <label className="add-agent-label">Test query</label>
      <input
        className="add-agent-input add-agent-sample"
        value={sample}
        placeholder={description || 'A question this agent should handle'}
        onChange={(e) => setSample(e.target.value)}
      />

      <div className="add-agent-actions">
        <button className="chat-suggest add-agent-test" disabled={!canTest || verifyState === 'busy'} onClick={runVerify}>
          {verifyState === 'busy' ? 'Verifying…' : 'Verify'}
        </button>
        <button className="chat-send add-agent-publish" disabled={!canPublish || publishing} onClick={publish}>
          {publishing ? 'Publishing…' : 'Publish'}
        </button>
      </div>

      {published && (
        <div className="chat-notice add-agent-ok" style={{ marginTop: 12 }}>
          <Icon name="check_circle" /> Published <b style={{ margin: '0 4px' }}>{published}</b> — it's now in Agents and the roster.
        </div>
      )}

      {verifyState && verifyState !== 'busy' && (
        <div className="add-agent-verify">
          {verifyState.results.map((r, i) => {
            const rt = Array.isArray(r.routed_to) ? r.routed_to : []
            return (
              <div key={i} className="add-agent-verify-row">
                <span className={`chat-chip ${rt.includes(slug) ? 'routed-ok' : 'routed-no'}`}>
                  <Icon name={rt.includes(slug) ? 'check_circle' : 'error' } />
                  {rt.length ? `routed to ${rt.join(', ')}` : 'not routed'}
                </span>
                <div className="ph-sub" style={{ marginTop: 6 }}>{r.message}</div>
              </div>
            )
          })}
          {!routed && (
            <div className="ph-sub">Heads up — the orchestrator didn't pick <b>{slug}</b> for this query. Routing is driven by the description, so you can sharpen it and re-test — or publish anyway and refine later.</div>
          )}
        </div>
      )}

      {error && <div className="chat-notice add-agent-error"><Icon name="error" />{error}</div>}
    </>
  )

  if (inline) {
    return <div className="panel add-agent-inline">{form}</div>
  }

  return (
    <div className="add-agent-overlay" onClick={onClose}>
      <motion.div
        className="panel add-agent-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="add-agent-head">
          <h3>New agent</h3>
          <button className="add-agent-close" onClick={onClose} aria-label="Close">
            <Icon name="close" />
          </button>
        </div>
        {form}
      </motion.div>
    </div>
  )
}
